import { Injectable } from '@angular/core';
import { Observable, ReplaySubject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class IndexDBStorageService {
  private db: IDBDatabase | null = null;
  private dbReady = new ReplaySubject<boolean>(1);

  private readonly dbName = 'DraftMessagesDB';
  private readonly storeName = 'drafts';

  constructor() {
    this.connectToDb();
  }

  // Waits until the database is ready
  whenReady(): Observable<boolean> {
    return this.dbReady.asObservable();
  }

  private connectToDb() {
    // 1. Open a connection to the database
    const request = indexedDB.open(this.dbName, 1);

    // 2. Handle errors
    request.onerror = (event) => {
      console.error('Database error:', request.error);
      this.dbReady.error(request.error);
    };

    // 3. Handle successful connection
    request.onsuccess = (event) => {
      this.db = request.result;
      console.log('Database connection successful.');
      this.dbReady.next(true);
      this.dbReady.complete();
    };

    // 4. Create the object store with custom key (no autoIncrement)
    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(this.storeName)) {
        // Using custom key instead of auto-increment
        db.createObjectStore(this.storeName);
        console.log('Object store "files" created with custom keys.');
      }
    };
  }

  // Save record with custom key
  saveRecord(key: string, data: any): Promise<string> {
    return new Promise((resolve, reject) => {
      if (!this.db) {
        return reject('Database not initialized.');
      }

      // 1. Start a transaction
      const transaction = this.db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);

      // 2. Use put() to save with custom key (overwrites if exists)
      const request = store.put(data, key);

      request.onsuccess = () => {
        console.log(`Record saved with key: ${key}`);
        resolve(key);
      };
      
      request.onerror = () => {
        console.error('Error saving record:', request.error);
        reject(request.error);
      };
    });
  }

  // Get record by custom key
  getRecord(key: string): Promise<any | undefined> {
    return new Promise((resolve, reject) => {
      if (!this.db) {
        return reject('Database not initialized.');
      }

      // 1. Start a transaction
      const transaction = this.db.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);

      // 2. Create a request to get the data by key
      const request = store.get(key);

      request.onsuccess = () => {
        if (request.result) {
          console.log(`Record found for key: ${key}`);
          resolve(request.result);
        } else {
          console.log(`No record found for key: ${key}`);
          resolve(undefined);
        }
      };
      
      request.onerror = () => {
        console.error('Error retrieving record:', request.error);
        reject(request.error);
      };
    });
  }

  // Check if record exists for given key
  hasRecord(key: string): Promise<boolean> {
    return new Promise((resolve, reject) => {
      if (!this.db) {
        return reject('Database not initialized.');
      }

      const transaction = this.db.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      const request = store.count(key);

      request.onsuccess = () => resolve(request.result > 0);
      request.onerror = () => reject(request.error);
    });
  }

  // Delete record by key
  deleteRecord(key: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.db) {
        return reject('Database not initialized.');
      }

      const transaction = this.db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const request = store.delete(key);

      request.onsuccess = () => {
        console.log(`Record deleted for key: ${key}`);
        resolve();
      };
      
      request.onerror = () => {
        console.error('Error deleting record:', request.error);
        reject(request.error);
      };
    });
  }

  // Get all keys
  getAllKeys(): Promise<string[]> {
    return new Promise((resolve, reject) => {
      if (!this.db) {
        return reject('Database not initialized.');
      }

      const transaction = this.db.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      const request = store.getAllKeys();

      request.onsuccess = () => resolve(request.result as string[]);
      request.onerror = () => reject(request.error);
    });
  }

  // Legacy methods for backward compatibility (if needed)
  addFile(data: any): Promise<number> {
    console.warn('addFile is deprecated. Use saveRecord with custom key instead.');
    const autoKey = Date.now().toString();
    return this.saveRecord(autoKey, data).then(() => parseInt(autoKey));
  }

  getFileById(id: string): Promise<any | undefined> {
    console.warn('getFileById is deprecated. Use getRecord with string key instead.');
    return this.getRecord(id);
  }

  getRecordsByDomainAndUser(domainId: string, userId: string, includeReply: boolean = false): Promise<Array<{ key: string; value: any }>> {
    return new Promise((resolve, reject) => {
      if (!this.db) {
        return reject('Database not initialized.');
      }

      const transaction = this.db.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      const request = store.openCursor();
      
      const records: Array<{ key: string; value: any }> = [];
      const prefixes = [
        `draft-${domainId}-${userId}-`,
        `draft-attach-${domainId}-${userId}-`,
        `draft-users-${domainId}-${userId}-`
      ];

      if (includeReply) {
        prefixes.push(`draft-reply-${domainId}-${userId}-`);
        prefixes.push(`draft-attach-reply-${domainId}-${userId}-`);
        prefixes.push(`draft-users-reply-${domainId}-${userId}-`);
      }

      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result;
        
        if (cursor) {
          const key = cursor.key as string;
          // Check if key starts with any of the prefixes
          if (prefixes.some(prefix => key.startsWith(prefix))) {
            records.push({
              key: key,
              value: cursor.value
            });
          }
          cursor.continue();
        } else {
          console.log(`Retrieved ${records.length} records for domain: ${domainId}, user: ${userId}`);
          resolve(records);
        }
      };
      
      request.onerror = () => {
        console.error('Error retrieving records:', request.error);
        reject(request.error);
      };
    });
  }
}